import logger from './vercelRuntimeLogger';

/**
 * Uploads a file to storage and returns the final URL
 * @param file - File to upload
 * @param fileName - Name to use for the uploaded file
 * @returns Promise<string> - Returns the final URL of the uploaded file
 */
export const uploadFileToStorage = async (
  file: File,
  fileName: string = "uploaded-model.glb"
): Promise<string> => {
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  
  logger.info('FileUpload', 'UPLOAD_START', {
    uploadId,
    fileName,
    fileSize: file.size,
    fileType: file.type,
    fileSizeMB: (file.size / (1024 * 1024)).toFixed(2)
  });
  
  try {
    // Get upload URL (use environment variable or fallback to correct endpoint)
    const uploadUrlEndpoint = process.env.REACT_APP_UPLOAD_URL || "https://3d-serverless-eight.vercel.app/api/get-upload-urls";
    
    logger.info('FileUpload', 'GET_UPLOAD_URL_START', {
      uploadId,
      endpoint: uploadUrlEndpoint,
      containerName: "3dstudio",
      fileName
    });
    
    const uploadResponse = await fetch(
      uploadUrlEndpoint,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          containerName: "3dstudio",
          fileNames: [fileName],
        }),
      }
    );

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      logger.error('FileUpload', 'GET_UPLOAD_URL_FAILED', 
        new Error(`Failed to get upload URL: ${uploadResponse.status}`), 
        {
          uploadId,
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          errorText
        }
      );
      throw new Error(`Failed to get upload URL: ${errorText}`);
    }
    
    const data = await uploadResponse.json();
    
    logger.info('FileUpload', 'GET_UPLOAD_URL_SUCCESS', {
      uploadId,
      uploadUrlsCount: data.uploadUrls?.length,
      hasUploadUrl: !!data.uploadUrls?.[0]
    });

    // Upload the file
    const uploadUrl = data.uploadUrls[0];
    const uploadStartTime = Date.now();
    
    logger.info('FileUpload', 'BLOB_UPLOAD_START', {
      uploadId,
      fileName,
      fileSize: file.size,
      contentType: file.type || "model/gltf-binary"
    });
    
    const blobUploadResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": file.type || "model/gltf-binary", // image type
      },
      body: file,
    });
    
    const uploadDuration = Date.now() - uploadStartTime;
    
    if (!blobUploadResponse.ok) {
      logger.error('FileUpload', 'BLOB_UPLOAD_FAILED',
        new Error(`Blob upload failed: ${blobUploadResponse.status}`),
        {
          uploadId,
          status: blobUploadResponse.status,
          statusText: blobUploadResponse.statusText,
          uploadDuration: `${uploadDuration}ms`
        }
      );
      throw new Error(`Failed to upload file to blob storage: ${blobUploadResponse.statusText}`);
    }
    
    logger.info('FileUpload', 'BLOB_UPLOAD_SUCCESS', {
      uploadId,
      uploadDuration: `${uploadDuration}ms`,
      uploadSpeed: `${(file.size / uploadDuration / 1024).toFixed(2)} KB/ms`
    });

    // Return clean URL without query parameters
    const finalUrl = uploadUrl.split('?')[0];
    
    logger.info('FileUpload', 'UPLOAD_COMPLETE', {
      uploadId,
      finalUrl,
      fileName,
      totalDuration: `${Date.now() - parseInt(uploadId.split('_')[1])}ms`
    });
    
    return finalUrl;
  } catch (error) {
    logger.error('FileUpload', 'UPLOAD_FAILED', error, {
      uploadId,
      fileName,
      fileSize: file.size
    });
    throw error instanceof Error ? error : new Error('Failed to upload file');
  }
}; 